/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from "@mui/material";
import { useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { RootState } from "../../../../store";
import { UserMode } from "../../../../interfaces/User";
import { useShiftAssignments } from "../../../../hooks/query/useShiftAssignments";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { formatTime12hr } from "../../../../helpers/formatters";
import { employeesSortedSelector } from "../../../../reducers/employee";
import { serviceProvidersSortedSelector } from "../../../../reducers/serviceProvider";
import { useAppSelector } from "../../../../store/hooks";
import * as calendarFns from "../../../../components/Calendar/functions/calendarFunctions";
import {
  toNepaliNumber,
  toNepaliDay
} from "../../../../components/Calendar/functions/calendarFunctions";

// eslint-disable-next-line import/prefer-default-export
export function UserShifts(): React.ReactNode {
  const userCreds = useSelector((state: RootState) => state.userContext.userCreds);
  const rc = useCurrentResourceCentre();
  const shifts = rc?.shifts || [];

  const employees = useAppSelector(employeesSortedSelector);
  const serviceProviders = useAppSelector(serviceProvidersSortedSelector);

  const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf("week"));

  const fromDate = currentWeekStart.format("YYYY-MM-DD");
  const toDate = currentWeekStart.clone().endOf("week").format("YYYY-MM-DD");

  const { data: shiftAssignments = [], isLoading } = useShiftAssignments({ fromDate, toDate });

  const currentUserDetails = useMemo(() => {
    if (userCreds.authenticable === UserMode.Employee) {
      return employees.find((e) => Number(e.id) === Number(userCreds.authenticableId));
    }
    if (userCreds.authenticable === UserMode.ServiceProvider) {
      return serviceProviders.find((sp) => Number(sp.id) === Number(userCreds.authenticableId));
    }
    return null;
  }, [employees, serviceProviders, userCreds]);

  const daysOfWeek = useMemo(() => {
    const days = [];
    const day = currentWeekStart.clone();
    for (let i = 0; i < 7; i += 1) {
      days.push(day.clone());
      day.add(1, "day");
    }
    return days;
  }, [currentWeekStart]);

  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().add(1, "week"));
  };

  const getShiftForDate = (dateString: string) => {
    const assignment = shiftAssignments.find((a) => {
      const isTarget =
        userCreds.authenticable === UserMode.Employee
          ? Number(a.employeeId) === Number(userCreds.authenticableId)
          : Number(a.serviceProviderId) === Number(userCreds.authenticableId);
      if (!isTarget) return false;

      const effDate = moment(a.effectiveDate).format("YYYY-MM-DD");
      const endDate = a.endDate ? moment(a.endDate).format("YYYY-MM-DD") : effDate;

      return dateString >= effDate && dateString <= endDate;
    });

    const assignedShift = assignment
      ? shifts.find((s) => Number(s.id) === Number(assignment.shiftId))
      : null;
    const defaultShift = currentUserDetails?.defaultShiftId
      ? shifts.find((s) => Number(s.id) === Number(currentUserDetails.defaultShiftId))
      : null;

    return {
      shift: assignedShift || defaultShift,
      isDefault: !assignedShift && !!defaultShift
    };
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">My Weekly Shifts</Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handlePrevWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography fontWeight="500">
            {currentWeekStart.format("MMM D")} -{" "}
            {currentWeekStart.clone().endOf("week").format("MMM D, YYYY")}
          </Typography>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            "& .MuiTableCell-root": {
              borderColor: "divider",
              py: 1.5
            },
            maxHeight: "calc(100vh - 200px)"
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Shift Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Shift Time</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Break Time</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daysOfWeek.map((day) => {
                const dateString = day.format("YYYY-MM-DD");
                const { shift, isDefault } = getShiftForDate(dateString);
                const isToday = day.isSame(moment(), "day");
                const bsDate = calendarFns.convertADtoBS(day);
                const isWeeklyOff = currentUserDetails?.defaultWeeklyOffs?.includes(day.day());

                return (
                  <TableRow
                    key={dateString}
                    sx={{
                      backgroundColor: isWeeklyOff ? "#FAFAFA" : "inherit"
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily:
                              "'Noto Sans Devanagari', 'Noto Sans', 'Noto Sans Display', 'Poppins', sans-serif"
                          }}
                        >
                          {toNepaliNumber(bsDate.bsDate)}
                        </Typography>

                        {isToday && (
                          <Chip
                            label="Today"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(76, 175, 80, 0.12)",
                              color: "rgba(76, 175, 80, 0.87)",
                              height: "20px",
                              fontSize: "0.75rem"
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontFamily:
                            "'Noto Sans Devanagari', 'Noto Sans', 'Noto Sans Display', 'Poppins', sans-serif",
                          fontSize: "0.875rem"
                        }}
                      >
                        {toNepaliDay(day.day())}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={!isDefault ? "bold" : "regular"}>
                        {isWeeklyOff ? "Weekly Off" : shift ? shift.name : "No Shift Assigned"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {!isWeeklyOff && shift ? (
                        <Typography variant="body2">
                          {formatTime12hr(shift.startTime)} - {formatTime12hr(shift.endTime)}
                        </Typography>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {!isWeeklyOff && shift && (shift.breakStartTime || shift.breakEndTime) ? (
                        <Typography variant="body2">
                          {formatTime12hr(shift.breakStartTime)} -{" "}
                          {formatTime12hr(shift.breakEndTime)}
                        </Typography>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isWeeklyOff ? (
                        <Chip
                          label="Weekly Off"
                          size="small"
                          sx={{
                            backgroundColor: "#F5F5F5",
                            color: "#616161",
                            fontWeight: 500,
                            fontSize: "0.75rem"
                          }}
                        />
                      ) : shift ? (
                        <Chip
                          label={isDefault ? "Default Shift" : "Specific Assignment"}
                          size="small"
                          sx={{
                            backgroundColor: isDefault ? "#E3F2FD" : "#E8F5E9",
                            color: isDefault ? "#1565C0" : "#2E7D32",
                            fontWeight: 500,
                            fontSize: "0.75rem"
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
